/**
 * File: apps/qbench-cp/reactapp/src/shared/configs/rbac/accessPolicies.js
 *
 * Purpose: Defines the RBAC access policy objects used to gate routes and UI elements by
 *     privilege.
 *
 * Scope: Route-level and UI-element-level access policies for the customer portal, consumed by
 *     `withAccessControl` and the `AccessControl`/`useAccessControl` machinery.
 *
 * Tags: customer-portal, portal-roles
 *
 * Overview: Defines a `bypass` policy (`privilege: null`, skipping access checks) and a
 *     `routePolicies` map keyed by route path (e.g. `orders`, `invoices/:id`, `quotations`)
 *     specifying required privileges from `shared/constants/permissions`. A parallel
 *     `uiPolicies` map covers finer-grained UI elements (bulk-action menus, create/update
 *     buttons, attachment upload modules), some reusing route policies and others declaring
 *     their own `all`-required privilege combinations. The final `policies` export groups
 *     `none`, `ui`, and `route` for consumption elsewhere.
 *
 * Exports: policies (default export) — `{ none, ui, route }` access policy lookup object.
 */

import permissions from 'shared/constants/permissions';

const { create, read, update } = permissions;
const bypass = {
  privilege: null,  // `null` bypasses access policy checking
};

/**
 * @type {Object.<string, import('shared/hocs/withAccessControl').AccessControlProps>}
 */
const routePolicies = {
  'createorder': {
    privilege: null,
  },
  'orders': bypass,
  'orders/:id': bypass,
  'invoices': {
    privilege: [read.invoice],
  },
  'invoices/:id': {
    all: true,
    privilege: [read.invoice],
  },
  'quotations': {
    privilege: [read.quotation],
  },
  'quotations/:id': {
    all: true,
    privilege: [read.quotation],
    // onDenied: (navigate) => navigate('/portal/'),
  },
  'sources': {
    privilege: [read.source],
  },
  'sources/:id': {
    privilege: [read.source],
  },
  'reports': {
    privilege: [read.report],
  },
  'projects': {
    privilege: [read.project],
  },
};

/**
 * @type {Object.<string, import('shared/hocs/withAccessControl').AccessControlProps>}
 */
const uiPolicies = {
  orderBulkActionMenu: routePolicies.orders,
  orderFilterBySampleSource: routePolicies.sources,
  quoteAcceptDeclineBtn: routePolicies.quotations,
  reportDownloadBtn: routePolicies.reports,
  reportBulkDownloadMenuItem: routePolicies.reports,
  sourceDropdownCreateBtn: {
    all: true,
    privilege: [read.source, create.source],
  },
  sourceUpdateBtn: {
    all: true,
    privilege: [read.source, update.source],
  },
  projectDropdownCreateBtn: {
    all: true,
    privilege: [read.project, create.project],
  },
  sourceAttachmentUploadModule: {
    privilege: [create.source, update.source],
  },
};

const policies = {
  none: bypass,
  ui: uiPolicies,
  route: routePolicies,
};

export default policies;
